import React, { useState } from "react";
import styled from "styled-components";
import "swiper/css";
import "swiper/css/navigation";
import { Swiper, SwiperSlide } from "swiper/react";
import { device, size } from "../../../constants";
import { useWindowSize } from "../../../hooks/useWindowResize";
import { SlideArrowNavigation } from "./SlideArrowNavigation";
import { SlideBulletNavigation } from "./SlideBulletNavigation";
import { useRouter } from "next/router";
import { useTranslation } from "react-i18next";

const RelatedPostsSection = styled.div`
  width: 100%;

  :is([data-theme="dark"] &) {
    background-color: ${({ theme }) => theme.colors["background-02"]};
  }
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  line-height: 32px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors["neutral-05"]};
  text-align: center;
  position: relative;
  padding-top: 24px;
  margin-bottom: 24px;
  padding-top: 16px;
  
  &:before {
    content: '';
    position: absolute;
    border-radius: 4px;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 32px;
    height: 4px;
    background-color: ${({ theme }) => theme.colors["branding-01"]};
  }
  
  @media ${device.laptop} {
    padding-top: 24px;
    margin-bottom: 40px;
    font-size: 28px;
    line-height: 120%;

    &:before {
      width: 48px;
    }
  }

  :is([data-theme="dark"] &) {
    color: ${({ theme }) => theme.colors["neutral-01"]};
  }
`;

const PostCard = styled.a`
  display: block;
  position: relative;
  width: 342px;
  height: 342px;
  border-radius: 8px;
  overflow: hidden;
  text-decoration: none;

  
  &:hover {
    text-decoration: none;
  }

  @media ${device.tablet} {
    width: 100%;
  }

  @media ${device.laptopL} {
    height: 300px;
    width: 400px;
  }
`;

const PostImage = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url(${(props) => props.image});
  background-size: cover;
  background-position: center;
  transition: transform 0.3s ease;

  ${PostCard}:hover & {
    transform: scale(1.05);
  }

  @media ${device.laptopL} {
    height: 300px;
    width: 400px;
  }
`;

const PostOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 16px;
  background: linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0) 100%);

  @media ${device.laptopL} {
    padding: 20px;
  }
`;

const PostTitle = styled.h3`
  font-size: 18px;
  font-weight: 500;
  color: #FFF;
  margin-bottom: 20px;
  line-height: 1.4;

  @media ${device.tablet} {
    font-size: 20px;
  }
`;

const ReadMore = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #92C83E;
  font-size: 14px;
  font-weight: 500;

  &:after {
    content: '→';
    transition: transform 0.3s ease;
  }

  ${PostCard}:hover &:after {
    transform: translateX(4px);
  }
`;

const StyledSwiper = styled(Swiper)`
  .swiper-button-next,
  .swiper-button-prev {
    color: #92C83E;
    
    &:after {
      font-size: 24px;
    }
  }

  .swiper-button-disabled {
    opacity: 0.35;
  }
`;

const NavigationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 24px;
  
  .arrows {
    display: none;
  }

  @media ${device.laptop} {
    justify-content: space-between;
    align-items: flex-start;
    margin-top: 8px;

    .arrows {
      display: block;
    }
  }
`;

export function RelatedPosts({ posts }) {
	const [activeBullet, setActiveBullet] = useState(0);
	useWindowSize();
	const router = useRouter();
	const { t } = useTranslation();

	if (!posts || posts.length === 0) return null;

	return (
		<RelatedPostsSection>
			<div className="container-blog">
				<SectionTitle>{t("blog.relatedPosts")}</SectionTitle>
				<StyledSwiper
					spaceBetween={24}
					slidesPerView={1}
					navigation={false}
					onSlideChange={(e) => {
						setActiveBullet(e.activeIndex);
					}}
					breakpoints={{
						[size.laptop]: {
							slidesPerView: 2,
						},
						[size.laptopL]: {
							slidesPerView: 3,
						},
					}}
				>
					{posts.map((post) => (
						<SwiperSlide key={post.slug}>
							<PostCard href={`/${router.query.locale}/blog/${post.slug}`}>
								<PostImage image={post.metadata?.imagem_inicio?.url} />
								<PostOverlay>
									<PostTitle>{post.title}</PostTitle>
									<ReadMore>{t("blog.postReadMore")}</ReadMore>
								</PostOverlay>
							</PostCard>
						</SwiperSlide>
					))}

					<NavigationContainer>
						<div className="bullets">
							<SlideBulletNavigation activeBullet={activeBullet} />
						</div>
						<div className="arrows">
							<SlideArrowNavigation />
						</div>
					</NavigationContainer>
				</StyledSwiper>
			</div>
		</RelatedPostsSection>
	);
}
